import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import Select from 'react-select';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

const ADMIN_PREFIX = '/admin';

// ─── Utility ──────────────────────────────────────────────────────────────────

function pluralPhases(n) {
  return n === 1 ? '1 phase' : `${n} phases`;
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function GroupDiplomaCourses() {
  const [diplomaCourses, setDiplomaCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Table controls
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // Diploma Course Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseOptions, setCourseOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Phases side-panel
  const [phasesModalOpen, setPhasesModalOpen] = useState(false);
  const [activeDiplomaCourse, setActiveDiplomaCourse] = useState(null);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const dcResp = await axiosInstance.get(`${ADMIN_PREFIX}/custom-diploma-courses`);
      setDiplomaCourses(dcResp.data?.diplomaCourses || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Load available (non-taken) courses when the modal opens.
  // When editing, pass the diploma course id so that record's course stays selectable.
  const loadCourseOptions = useCallback(async (excludeDiplomaCourseId = null) => {
    try {
      const params = excludeDiplomaCourseId ? { exclude: excludeDiplomaCourseId } : {};
      const cResp = await axiosInstance.get(`${ADMIN_PREFIX}/custom-diploma-courses/available-courses`, { params });
      const courses = cResp.data?.courses || [];
      const opts = courses.map((c) => ({
        value: c.courseId,
        label: `${c.fullName}${c.shortName ? ` (${c.shortName})` : ''}`,
      }));
      setCourseOptions(opts);
      return opts;
    } catch {
      setCourseOptions([]);
      return [];
    }
  }, []);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    if (!term) return diplomaCourses;
    return diplomaCourses.filter(
      (dc) =>
        String(dc.courseName || '').toLowerCase().includes(term) ||
        String(dc.courseShortName || '').toLowerCase().includes(term)
    );
  }, [diplomaCourses, searchTerm]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset to page 1 when search changes
  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  // ── Modal helpers ──────────────────────────────────────────────────────────

  const openAdd = async () => {
    setEditingItem(null);
    setSelectedCourse(null);
    setCourseOptions([]);
    setFormError('');
    setModalOpen(true);
    await loadCourseOptions(null);
  };

  const openEdit = async (item) => {
    setEditingItem(item);
    setSelectedCourse(null);
    setCourseOptions([]);
    setFormError('');
    setModalOpen(true);
    // Load courses excluding other diploma-course records so the current one stays selectable
    const opts = await loadCourseOptions(item.id);
    // Pre-select the current course
    setSelectedCourse(
      (opts || courseOptions).find((o) => o.value === item.course_id) || null
    );
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setSelectedCourse(null);
    setFormError('');
  };

  // ── CRUD handlers ──────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCourse) {
      setFormError('Please select a course');
      return;
    }
    setFormError('');
    setSubmitting(true);
    try {
      if (editingItem) {
        await axiosInstance.put(`${ADMIN_PREFIX}/custom-diploma-courses/${editingItem.id}`, {
          course_id: selectedCourse.value,
        });
        Swal.fire({ icon: 'success', title: 'Updated!', text: 'Diploma course updated.', timer: 1800, showConfirmButton: false });
      } else {
        await axiosInstance.post(`${ADMIN_PREFIX}/custom-diploma-courses`, {
          course_id: selectedCourse.value,
        });
        Swal.fire({ icon: 'success', title: 'Created!', text: 'Diploma course added.', timer: 1800, showConfirmButton: false });
      }
      closeModal();
      loadData();
    } catch (err) {
      setFormError(err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    const result = await Swal.fire({
      title: 'Delete Diploma Course?',
      html: `<span>This will permanently remove <strong>${item.courseName}</strong> and all its phases.</span>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    try {
      await axiosInstance.delete(`${ADMIN_PREFIX}/custom-diploma-courses/${item.id}`);
      Swal.fire({ icon: 'success', title: 'Deleted', text: 'Diploma course removed.', timer: 1800, showConfirmButton: false });
      loadData();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to delete', 'error');
    }
  };

  const openPhases = (item) => {
    setActiveDiplomaCourse(item);
    setPhasesModalOpen(true);
  };

  // ── Pagination ─────────────────────────────────────────────────────────────

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);

    return (
      <div className="flex justify-center mt-6">
        <nav className="flex items-center space-x-1">
          <button
            onClick={() => setCurrentPage((p) => p - 1)}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ‹ Prev
          </button>
          {pages.map((n) => (
            <button
              key={n}
              onClick={() => setCurrentPage(n)}
              className={`px-3 py-2 text-sm font-medium border ${
                currentPage === n
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
              }`}
            >
              {n}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((p) => p + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next ›
          </button>
        </nav>
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">

        {/* Page header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Group Diploma Courses</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage diploma course registrations and their examination phases.
          </p>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard
            label="Total Diploma Courses"
            value={diplomaCourses.length}
            colorClass="bg-indigo-100 text-indigo-700"
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            }
          />
          <StatCard
            label="Total Phases Defined"
            value={diplomaCourses.reduce((acc, dc) => acc + Number(dc.phaseCount || 0), 0)}
            colorClass="bg-emerald-100 text-emerald-700"
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            }
          />
          <StatCard
            label="Courses Without Phases"
            value={diplomaCourses.filter((dc) => Number(dc.phaseCount || 0) === 0).length}
            colorClass="bg-amber-100 text-amber-700"
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            }
          />
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-5">
          <div className="relative flex-1 max-w-sm">
            <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by course name…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
          </div>
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Diploma Course
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['#', 'Course', 'Short Name', 'Phases', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className={`px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider ${h === 'Actions' ? 'text-right' : ''}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center">
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                      <svg className="animate-spin w-6 h-6" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      <span className="text-sm">Loading…</span>
                    </div>
                  </td>
                </tr>
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center text-sm text-gray-400">
                    {searchTerm ? 'No results match your search.' : 'No diploma courses yet. Click "Add Diploma Course" to get started.'}
                  </td>
                </tr>
              ) : (
                paginated.map((dc, idx) => (
                  <tr key={dc.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-400">
                      {(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 flex-shrink-0 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-indigo-700 text-xs font-bold">
                            {(dc.courseShortName || dc.courseName || '?').slice(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{dc.courseName || '—'}</p>
                          <p className="text-xs text-gray-400">ID #{dc.id}</p>
                          {dc.courseScheduleType && (
                            <span className={`mt-0.5 inline-block text-xs font-medium px-1.5 py-0.5 rounded ${
                              dc.courseScheduleType === 'weekend'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-sky-100 text-sky-700'
                            }`}>
                              {dc.courseScheduleType.charAt(0).toUpperCase() + dc.courseScheduleType.slice(1)}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-600">
                      {dc.courseShortName || '—'}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          Number(dc.phaseCount) > 0
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-600'
                        }`}
                      >
                        {pluralPhases(Number(dc.phaseCount || 0))}
                      </span>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-right">
                      <div className="inline-flex items-center gap-2">
                        <ActionButton
                          onClick={() => openPhases(dc)}
                          colorClass="text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                          label="Phases"
                          icon={
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                          }
                        />
                        <ActionButton
                          onClick={() => openEdit(dc)}
                          colorClass="text-amber-700 bg-amber-50 hover:bg-amber-100"
                          label="Edit"
                          icon={
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          }
                        />
                        <ActionButton
                          onClick={() => handleDelete(dc)}
                          colorClass="text-red-700 bg-red-50 hover:bg-red-100"
                          label="Delete"
                          icon={
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          }
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && filtered.length > 0 && (
          <p className="mt-3 text-xs text-gray-400 text-right">
            Showing {Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, filtered.length)}–
            {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} of {filtered.length} entries
          </p>
        )}

        {renderPagination()}
      </div>

      {/* ── Add / Edit Modal ── */}
      {modalOpen && (
        <DiplomaModal
          editing={editingItem}
          courseOptions={courseOptions}
          selectedCourse={selectedCourse}
          onCourseChange={setSelectedCourse}
          formError={formError}
          submitting={submitting}
          onSubmit={handleSubmit}
          onClose={closeModal}
        />
      )}

      {/* ── Phases Management Modal ── */}
      {phasesModalOpen && activeDiplomaCourse && (
        <PhasesModal
          diplomaCourse={activeDiplomaCourse}
          onClose={() => {
            setPhasesModalOpen(false);
            setActiveDiplomaCourse(null);
            loadData(); // refresh phase counts
          }}
        />
      )}
    </DashboardLayout>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, colorClass, icon }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
      <div className={`p-3 rounded-xl ${colorClass}`}>{icon}</div>
      <div>
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  );
}

function ActionButton({ onClick, colorClass, label, icon }) {
  return (
    <button
      onClick={onClick}
      title={label}
      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${colorClass}`}
    >
      {icon}
      {label}
    </button>
  );
}

// ── Diploma Course Add/Edit Modal ─────────────────────────────────────────────

function DiplomaModal({ editing, courseOptions, selectedCourse, onCourseChange, formError, submitting, onSubmit, onClose }) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {editing ? 'Edit Diploma Course' : 'Add Diploma Course'}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {editing ? 'Update the linked course' : 'Register a course as a group diploma course'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} className="px-6 py-5 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Course <span className="text-red-500">*</span>
            </label>
            <Select
              options={courseOptions}
              value={selectedCourse}
              onChange={onCourseChange}
              placeholder="Select a course…"
              isClearable
              classNamePrefix="react-select"
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: state.isFocused ? '#6366f1' : '#d1d5db',
                  boxShadow: state.isFocused ? '0 0 0 3px rgba(99,102,241,.15)' : 'none',
                  borderRadius: '0.5rem',
                  minHeight: '40px',
                  fontSize: '14px',
                }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
            {formError && (
              <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {formError}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {submitting && (
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
              )}
              {editing ? 'Save Changes' : 'Add Course'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Phases Management Modal ───────────────────────────────────────────────────

function PhasesModal({ diplomaCourse, onClose }) {
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);

  // Phase form
  const [phaseFormOpen, setPhaseFormOpen] = useState(false);
  const [editingPhase, setEditingPhase] = useState(null);
  const [phaseName, setPhaseName] = useState('');
  const [phaseOrder, setPhaseOrder] = useState('');
  const [phaseError, setPhaseError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadPhases = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axiosInstance.get(
        `${ADMIN_PREFIX}/custom-diploma-courses/${diplomaCourse.id}/phases`
      );
      setPhases(resp.data?.phases || []);
    } catch {
      setPhases([]);
    } finally {
      setLoading(false);
    }
  }, [diplomaCourse.id]);

  useEffect(() => { loadPhases(); }, [loadPhases]);

  const openPhaseAdd = () => {
    setEditingPhase(null);
    setPhaseName('');
    setPhaseOrder(String((phases.length || 0) + 1));
    setPhaseError('');
    setPhaseFormOpen(true);
  };

  const openPhaseEdit = (phase) => {
    setEditingPhase(phase);
    setPhaseName(phase.phase_name);
    setPhaseOrder(String(phase.phase_order));
    setPhaseError('');
    setPhaseFormOpen(true);
  };

  const closePhaseForm = () => {
    setPhaseFormOpen(false);
    setEditingPhase(null);
    setPhaseName('');
    setPhaseOrder('');
    setPhaseError('');
  };

  const handlePhaseSubmit = async (e) => {
    e.preventDefault();
    if (!phaseName.trim()) { setPhaseError('Phase name is required'); return; }
    const order = parseInt(phaseOrder, 10);
    if (!order || order < 1) { setPhaseError('Phase order must be a positive number'); return; }
    setPhaseError('');
    setSubmitting(true);
    try {
      if (editingPhase) {
        await axiosInstance.put(
          `${ADMIN_PREFIX}/custom-diploma-courses/${diplomaCourse.id}/phases/${editingPhase.id}`,
          { phase_name: phaseName.trim(), phase_order: order }
        );
      } else {
        await axiosInstance.post(
          `${ADMIN_PREFIX}/custom-diploma-courses/${diplomaCourse.id}/phases`,
          { phase_name: phaseName.trim(), phase_order: order }
        );
      }
      closePhaseForm();
      loadPhases();
    } catch (err) {
      setPhaseError(err.response?.data?.message || 'Failed to save phase');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePhase = async (phase) => {
    const result = await Swal.fire({
      title: 'Delete Phase?',
      text: `"${phase.phase_name}" will be permanently removed.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(
        `${ADMIN_PREFIX}/custom-diploma-courses/${diplomaCourse.id}/phases/${phase.id}`
      );
      loadPhases();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to delete phase', 'error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-xl rounded-2xl shadow-xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Examination Phases</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              <span className="font-medium text-indigo-600">{diplomaCourse.courseName}</span>
              {diplomaCourse.courseShortName && (
                <span className="ml-1 text-gray-400">({diplomaCourse.courseShortName})</span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

          {/* Inline phase form */}
          {phaseFormOpen && (
            <form onSubmit={handlePhaseSubmit} className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-semibold text-indigo-800">
                {editingPhase ? 'Edit Phase' : 'New Phase'}
              </h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Phase Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={phaseName}
                    onChange={(e) => setPhaseName(e.target.value)}
                    placeholder="e.g. Written Exam"
                    maxLength={255}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Order <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={phaseOrder}
                    onChange={(e) => setPhaseOrder(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  />
                </div>
              </div>
              {phaseError && (
                <p className="text-xs text-red-600">{phaseError}</p>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closePhaseForm}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-60"
                >
                  {submitting && (
                    <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                  )}
                  {editingPhase ? 'Update' : 'Add Phase'}
                </button>
              </div>
            </form>
          )}

          {/* Phases list */}
          {loading ? (
            <div className="flex justify-center py-8 text-gray-400">
              <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            </div>
          ) : phases.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-400">
              <svg className="w-10 h-10 mx-auto mb-2 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              No phases defined yet.
            </div>
          ) : (
            <div className="space-y-2">
              {phases.map((phase) => (
                <div
                  key={phase.id}
                  className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 hover:border-indigo-200 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex-shrink-0 h-7 w-7 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">
                      {phase.phase_order}
                    </span>
                    <span className="text-sm font-medium text-gray-800">{phase.phase_name}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => openPhaseEdit(phase)}
                      className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50 transition-colors"
                      title="Edit phase"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeletePhase(phase)}
                      className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                      title="Delete phase"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <span className="text-xs text-gray-400">{pluralPhases(phases.length)}</span>
          <div className="flex gap-2">
            {!phaseFormOpen && (
              <button
                onClick={openPhaseAdd}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Phase
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
